import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { Village } from '../../../core/data/types/stateDistrict';
import { addVillage, updateVillage, fetchVillages } from '../../../core/data/services/stateDistrictBlockService';
import { setVillages, setLoading, setError } from '../../../core/data/redux/stateDistrictSlice';

interface VillageFormProps {
  show: boolean;
  onHide: () => void;
  village?: Village;
  stateId: number;
  districtId: number;
  blockAreaId: number;
}

const VillageForm: React.FC<VillageFormProps> = ({ show, onHide, village, blockAreaId, stateId, districtId }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState<Omit<Village, 'id'> | Village>({
    name: '',
    blockAreaId, stateId, districtId
  });
  const [validated, setValidated] = useState(false);

  useEffect(() => {
    if (village) {
      setFormData(village);
    } else {
      setFormData({ name: '', blockAreaId, stateId, districtId });
    }
  }, [village, blockAreaId, stateId, districtId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    dispatch(setLoading(true));
    try {
      let response;
      // Ensure districtId is a number in the payload
      const payload = {
        ...formData,
        districtId: Number(formData.districtId)
      };
      if (village) {
        // Update existing village
        response = await updateVillage({ ...payload, id: village.id });
        dispatch(setVillages([])); // Clear villages to trigger refetch
      } else {
        // Add new village
        response = await addVillage(payload);
      }
      // Refetch villages for the selected block
      const updatedVillages = await fetchVillages(stateId, districtId, blockAreaId);
      dispatch(setVillages(updatedVillages));
      onHide();
      setValidated(false);
    } catch (err: any) {
      dispatch(setError(err.message || 'Failed to save village'));
    } finally {
      dispatch(setLoading(false));
      setFormData({ name: '', blockAreaId, stateId, districtId });
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{village ? 'Edit Village' : 'Add Village'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form noValidate validated={validated} onSubmit={handleSubmit}>
          <Form.Group className="mb-3" controlId="villageName">
            <Form.Label>Village Name</Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Enter village name"
            />
            <Form.Control.Feedback type="invalid">
              Please provide a village name.
            </Form.Control.Feedback>
          </Form.Group>
          <Modal.Footer className='gap-2'>
            <Button variant="secondary" onClick={onHide}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" >
              {village ? 'Update' : 'Add'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default VillageForm;